/**
 * baba's own on-device Hebrew voice — Pocket TTS, running on onnxruntime-web.
 *
 * The web counterpart of mobile's `services/pocketTts` (see that module's
 * header comment for the full picture). Opt-in and off by default: nothing
 * downloads until `download()` is called from the Settings row, and nothing
 * here ever counts against a usage limit or requires Pro — once the voice is
 * on disk, it works for guests, free users and Pro alike.
 */
import type { SpeakError, SpeakRequest, SpeakResult } from './worker'
import * as store from './modelStore'
import type { VoiceGender } from '../baba-api'

export type { DownloadProgress } from './modelStore'
export { voiceFor, canSpeakOffline, VOICES } from './voices'
import { canSpeakOffline, voiceFor } from './voices'

/**
 * The one switch that decides whether this voice exists in the product.
 *
 * ON again from 2026-09-23. It was off from 2026-09-20 while the model card
 * at `huggingface.co/thewh1teagle/pocket-tts-onnx` said `cc-by-nc-4.0`; the
 * author has since confirmed (pocket-tts-onnx#2) that the tag was a mistake
 * and every weight file, `he/model.onnx`, its adapter and `renikud.onnx`, is
 * CC BY 4.0, commercial use included, with credit. Both model cards now say
 * so. See `vendor/README.md`.
 *
 * Kept as a switch rather than deleted: off, nobody can start a download and
 * anyone who has the model is offered Remove. The voice people hear by default
 * is still ElevenLabs through the gateway; see `speakWithChoice` for when this
 * one speaks instead.
 */
export const OFFLINE_VOICE_ENABLED = true

/** Cheap, synchronous hint for UI that can't await — mirrors the flag `download()` sets. */
export const isReadySync = store.isReadySync
/** Authoritative check: every asset the engine needs is actually cached. */
export const isReady = store.isReady
/**
 * The check every caller must use before falling through to a network
 * voice — bounded, and skips the Cache API entirely when `isReadySync()`
 * already says there's nothing downloaded. See `modelStore.ts` for why
 * `isReady()` alone is unsafe to gate a fallback on.
 */
export const isReadyGuarded: typeof store.isReadyGuarded = async () =>
  OFFLINE_VOICE_ENABLED ? store.isReadyGuarded() : false
export const download = store.download

/** Delete the model, and with it the choice to use it. */
export async function remove(): Promise<void> {
  await store.removeAll()
  setEngine('cloud')
}

/**
 * Settings' voice choice: the studio voice (ElevenLabs, online) or this one.
 * Whichever is picked plays first and the other is the fallback — see
 * `speakWithChoice`. The same choice exists on iOS and Android.
 */
export type VoiceEngine = 'cloud' | 'device'

const ENGINE_KEY = 'baba.tts_engine'

export function getEngine(): VoiceEngine {
  if (!OFFLINE_VOICE_ENABLED) return 'cloud'
  try {
    return localStorage.getItem(ENGINE_KEY) === 'device' ? 'device' : 'cloud'
  } catch {
    return 'cloud'
  }
}

export function setEngine(engine: VoiceEngine): void {
  try {
    localStorage.setItem(ENGINE_KEY, engine)
  } catch {
    /* storage unavailable — the choice lasts for this page only */
  }
}

let worker: Worker | null = null
let nextId = 1
const pending = new Map<number, { resolve: (r: SpeakResult) => void; reject: (err: Error) => void }>()

function getWorker(): Worker {
  if (worker) return worker
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
  worker.onmessage = (event: MessageEvent<SpeakResult | SpeakError>) => {
    const data = event.data
    const entry = pending.get(data.id)
    if (!entry) return
    pending.delete(data.id)
    if (data.type === 'error') entry.reject(new Error(data.message))
    else entry.resolve(data)
  }
  worker.onerror = (event) => {
    // A worker-level failure (e.g. the module failed to load) has no request
    // id to match — reject everything still waiting rather than hang forever.
    for (const [id, entry] of pending) {
      entry.reject(new Error(event.message || 'Offline voice worker crashed'))
      pending.delete(id)
    }
  }
  return worker
}

/**
 * Load the model in the worker before the first Listen tap, for someone who
 * picked the on-device voice. The load is most of a cold tap's wait, and it
 * holds ~200 MB, so it only happens for people who chose this voice.
 */
export function warm(): void {
  if (getEngine() !== 'device' || !isReadySync()) return
  getWorker().postMessage({ type: 'warm' })
}

function requestSynthesis(text: string, voice: string, phonemes: boolean): Promise<SpeakResult> {
  const id = nextId++
  const request: SpeakRequest = { id, type: 'speak', text, voice, phonemes }
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject })
    getWorker().postMessage(request)
  })
}

export interface SpokenAudio {
  audioUrl: string
}

/**
 * Synthesize a line and hand back a playable `blob:` URL, same shape as
 * `generateSpeech` in `lib/baba-api.ts` — so the caller can drop it straight
 * into `new Audio(audioUrl)` exactly as it does for the network voice.
 *
 * Throws if the voice isn't downloaded, the line doesn't qualify (mixed
 * Hebrew/Latin — check `canSpeakOffline` first), or the worker fails. The
 * caller's job is to catch that and fall back to the network voice.
 */
export async function speak(
  text: string,
  opts: { language: string; gender: VoiceGender },
): Promise<SpokenAudio> {
  if (!(await store.isReady())) throw new Error('The offline voice has not been downloaded')
  const voice = voiceFor(opts.language, opts.gender)
  const result = await requestSynthesis(text, voice.name, voice.phonemes)
  const blob = new Blob([result.wav], { type: 'audio/wav' })
  return { audioUrl: URL.createObjectURL(blob) }
}

/**
 * On-device audio for this line, or `null` when the voice is not downloaded,
 * cannot take the line (Hebrew mixed with Latin), or fails to render.
 */
async function trySpeak(text: string, opts: { language: string; gender: VoiceGender }): Promise<SpokenAudio | null> {
  if (!OFFLINE_VOICE_ENABLED) return null
  if (!canSpeakOffline(text, opts.language)) return null
  if (!(await isReadyGuarded())) return null
  try {
    return await speak(text, opts)
  } catch {
    return null
  }
}

/**
 * The one voice policy every Listen button uses.
 *
 * With the on-device voice picked in Settings (or no connection at all), it
 * speaks first, for every tier and with no allowance spent. Otherwise, or when
 * it cannot take the line, `cloud` runs — the caller's ElevenLabs request,
 * including its own sign-in and Pro checks, which it signals by throwing the
 * same `{ type }` errors `generateSpeech` raises. If that throws for any
 * reason (signed out, not Pro, out of characters, offline, server error) and
 * the voice is downloaded, it speaks after all: the model costs nothing per
 * play, so it is free for everyone who fetched it. Only when there is no
 * on-device audio to give does the caller's error come back to it.
 */
export async function speakWithChoice(opts: {
  text: string
  language: string
  gender: VoiceGender
  cloud: () => Promise<SpokenAudio>
}): Promise<SpokenAudio & { onDevice: boolean }> {
  const voice = { language: opts.language, gender: opts.gender }
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  let triedDevice = false
  if (getEngine() === 'device' || offline) {
    triedDevice = true
    const local = await trySpeak(opts.text, voice)
    if (local) return { ...local, onDevice: true }
  }
  try {
    return { ...(await opts.cloud()), onDevice: false }
  } catch (err) {
    const local = triedDevice ? null : await trySpeak(opts.text, voice)
    if (local) return { ...local, onDevice: true }
    throw err
  }
}
